import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useApiQuery, useFetch, useTableDataQuery } from '@/modules/common';
import { PaginatedResponse } from '@/modules/common/types';

import { InventoryReportType } from '../constants';
import { EventProductResponseItem } from '../types';

const fileNameMap = {
  [InventoryReportType.CheckedOutBags]: 'Checked-Out Bags Report',
  [InventoryReportType.BackStockInventory]: 'Back Stock Inventory Report',
};

export function useEventProducts(eventId: string, stationId: string) {
  const fetchClient = useFetch();
  const queryClient = useQueryClient();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const baseUrl = `/events/${eventId}/stations/${stationId}/products`;

  const {
    data: products,
    isFetching,
    ...restTableProps
  } = useTableDataQuery<EventProductResponseItem>({
    url: baseUrl,
    // @ts-ignore
    queryOptions: {
      enabled: !!eventId && !!stationId,
    },
  });

  const uploadEventProducts = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetchClient(baseUrl, {
        body: formData,
        method: 'PATCH',
        asFormData: true,
      });
      queryClient.invalidateQueries({
        queryKey: ['eventDetails', String(eventId)],
      });
    } catch (error) {
      toast.error(error.message);
      throw new Error(error.message);
    }
  };

  const getGenerateReportUrl = async (reportType: InventoryReportType) => {
    setIsGeneratingReport(true);
    try {
      const response = await fetchClient(`${baseUrl}/report/${reportType}`, {
        method: 'GET',
        asFormData: true,
        responseType: 'blob',
      });
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileNameMap[reportType]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return {
    isFetching,
    uploadEventProducts,
    getGenerateReportUrl,
    isGeneratingReport,
    products,
    restTableProps,
  };
}

// Every product of a station in one go, for pickers that need the full list
// instead of a page of it (product sets modal). Mirrors the `pageSize=1000`
// approach used by useFetchModelsData for /gcrs.
export function useStationProducts(
  eventId: string,
  stationId: string,
  enabled = true
) {
  const { data, isFetching } = useApiQuery<
    PaginatedResponse<EventProductResponseItem>
  >(`/events/${eventId}/stations/${stationId}/products?page=1&pageSize=1000`, {
    queryKey: ['stationProducts', eventId, stationId],
    initialData: null,
    enabled: !!eventId && !!stationId && enabled,
  });

  return {
    products: data?.items ?? [],
    isFetching,
  } as const;
}
